import React, { useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Download, ArrowUp, ArrowDown, Minus,
} from 'lucide-react';
import Tooltip from '../../../components/ui/Tooltip';
import {
  KpiLabel, KpiValue, KpiSub, ExportBtn, Skeleton,
} from '../styles';
import { KPI_ACCENTS, DELTA, CARD_IDS } from '../constants';
import { exportCardPng } from '../exportCardPng';
import {
  formatDuration, formatNumber, formatPercent, deltaView,
} from '../format';
import { buildDrillLink } from '../../../utils/attendanceFilterQuery';

const KpiGrid = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Tile = styled.div`
  position: relative;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  border-left: 4px solid ${({ $accent }) => $accent};
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 96px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: box-shadow 0.15s ease;

  ${({ $clickable }) => $clickable && `
    &:hover { box-shadow: 0 2px 10px rgba(0, 66, 66, 0.14); }
    &:focus-visible { outline: 2px solid #2a78d6; outline-offset: 2px; }
  `}
`;

const TileTop = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const OpenList = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #2a78d6;
`;

const DeltaText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`;

const Muted = styled.span`
  color: #6B7280;
`;

function DeltaBadge({ view, text, vsLabel }) {
  if (!view) return null;
  const Icon = view.direction === 'up' ? ArrowUp : view.direction === 'down' ? ArrowDown : Minus;
  return (
    <>
      <DeltaText $color={DELTA[view.tone]} data-delta={view.tone}>
        <Icon size={12} />
        {text}
      </DeltaText>
      <Muted>{vsLabel}</Muted>
    </>
  );
}

function KpiTile({
  kind, cardKey, label, value, sub, loading, failed, onOpen,
}) {
  const { t } = useTranslation();
  const ref = useRef(null);

  const handleExport = useCallback((event) => {
    event.stopPropagation();
    exportCardPng(ref.current, cardKey).catch(() => {});
  }, [cardKey]);

  const clickable = Boolean(onOpen);
  const handleKey = useCallback((event) => {
    if (event.key === 'Enter' && onOpen) onOpen();
  }, [onOpen]);

  return (
    <Tile
      ref={ref}
      $accent={KPI_ACCENTS[kind]}
      $clickable={clickable}
      data-kpi={kind}
      role={clickable ? 'link' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={clickable ? handleKey : undefined}
    >
      <TileTop data-export-exclude>
        {clickable ? <OpenList>{t('attendanceDashboard.kpi.openList')}</OpenList> : null}
        <Tooltip text={t('attendanceDashboard.export.saveAsPng')}>
          <ExportBtn type="button" onClick={handleExport} aria-label={t('attendanceDashboard.export.saveAsPng')}>
            <Download size={13} />
          </ExportBtn>
        </Tooltip>
      </TileTop>
      <KpiLabel>{label}</KpiLabel>
      {loading ? <Skeleton $h="42px" /> : failed ? <KpiSub>{t('attendanceDashboard.loadFailed')}</KpiSub> : (
        <>
          <KpiValue>{value}</KpiValue>
          <KpiSub>{sub}</KpiSub>
        </>
      )}
    </Tile>
  );
}

// 5 gösterge: Devam Oranı, Geç Kalan, Erken Çıkan, Devamsız, Eksik Çalışan. Son dördü ilgili listeyi panelin
// süzgeçleriyle ve göstergenin saydığı aralıkla açar — panelde "3 kez", listede 3 satır: geç/erken bugüne kadar
// (summary.effectiveTo), devamsız/eksik düne kadar (summary.closedTo — bugün henüz bitmedi). Geçmiş günü olmayan
// (gelecek) dönemde ya da özet yüklenemediğinde bağlantı yoktur (inceleme M6).
export default function KpiRow({ slice, query }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const lang = i18n.language;
  const s = slice.data;
  const loading = slice.loading && !s;
  const failed = slice.failed;
  const vsLabel = s?.isCalendarMonth ? t('attendanceDashboard.kpi.vsPrevMonth') : t('attendanceDashboard.kpi.vsPrevPeriod');

  const elapsed = Boolean(s && !failed && s.effectiveTo >= s.from);
  const closed = Boolean(s && !failed && s.closedTo >= s.from);
  const open = useCallback((kind, to) => {
    history.push(buildDrillLink(kind, query, { to }));
  }, [history, query]);
  const opener = (kind, enabled, to) => (query && enabled ? () => open(kind, to) : undefined);

  const percentDelta = (kind, kpi) => {
    const view = deltaView(kind, kpi);
    return view ? (
      <DeltaBadge
        view={view}
        text={t('attendanceDashboard.kpi.deltaPercent', { value: formatNumber(Math.abs(view.value), lang, 1) })}
        vsLabel={vsLabel}
      />
    ) : null;
  };

  const rateView = deltaView('rate', s?.rate);
  const absentView = deltaView('absent', s?.absent);

  return (
    <KpiGrid id={CARD_IDS.kpi}>
      <KpiTile
        kind="rate"
        cardKey="devam-orani"
        loading={loading}
        failed={failed}
        label={t('attendanceDashboard.kpi.attendanceRate')}
        value={formatPercent(s?.rate?.percent, lang)}
        sub={(
          <>
            <Muted>{t('attendanceDashboard.kpi.scheduledDays', { count: s?.rate?.scheduledDays ?? 0 })}</Muted>
            {rateView ? (
              <DeltaBadge
                view={rateView}
                text={t('attendanceDashboard.kpi.points', { value: formatNumber(Math.abs(rateView.value), lang, 1) })}
                vsLabel={vsLabel}
              />
            ) : null}
          </>
        )}
      />
      <KpiTile
        kind="late"
        cardKey="gec-kalan"
        loading={loading}
        failed={failed}
        onOpen={opener('late', elapsed, s?.effectiveTo)}
        label={t('attendanceDashboard.kpi.late')}
        value={t('attendanceDashboard.kpi.people', { count: s?.late?.people ?? 0 })}
        sub={(
          <>
            <Muted>
              {t('attendanceDashboard.kpi.timesMinutes', {
                times: formatNumber(s?.late?.occurrences, lang), minutes: formatNumber(s?.late?.minutes, lang),
              })}
            </Muted>
            {percentDelta('late', s?.late)}
          </>
        )}
      />
      <KpiTile
        kind="early"
        cardKey="erken-cikan"
        loading={loading}
        failed={failed}
        onOpen={opener('early', elapsed, s?.effectiveTo)}
        label={t('attendanceDashboard.kpi.early')}
        value={t('attendanceDashboard.kpi.people', { count: s?.early?.people ?? 0 })}
        sub={(
          <>
            <Muted>
              {t('attendanceDashboard.kpi.timesMinutes', {
                times: formatNumber(s?.early?.occurrences, lang), minutes: formatNumber(s?.early?.minutes, lang),
              })}
            </Muted>
            {percentDelta('early', s?.early)}
          </>
        )}
      />
      <KpiTile
        kind="absent"
        cardKey="devamsiz"
        loading={loading}
        failed={failed}
        onOpen={opener('absent', closed, s?.closedTo)}
        label={t('attendanceDashboard.kpi.absent')}
        value={t('attendanceDashboard.kpi.people', { count: s?.absent?.people ?? 0 })}
        sub={(
          <>
            <Muted>{t('attendanceDashboard.kpi.days', { count: s?.absent?.days ?? 0 })}</Muted>
            {absentView ? (
              <DeltaBadge
                view={absentView}
                text={t('attendanceDashboard.kpi.deltaDays', { count: Math.abs(absentView.value) })}
                vsLabel={vsLabel}
              />
            ) : null}
          </>
        )}
      />
      <KpiTile
        kind="undertime"
        cardKey="eksik-calisan"
        loading={loading}
        failed={failed}
        onOpen={opener('undertime', closed, s?.closedTo)}
        label={t('attendanceDashboard.kpi.undertime')}
        value={t('attendanceDashboard.kpi.people', { count: s?.undertime?.people ?? 0 })}
        sub={(
          <>
            <Muted>{t('attendanceDashboard.kpi.missing', { duration: formatDuration(s?.undertime?.minutes ?? 0, t) })}</Muted>
            {percentDelta('undertime', s?.undertime)}
          </>
        )}
      />
    </KpiGrid>
  );
}
